import { forwardRef } from 'react';

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';

import { cn } from '../../lib/cn';

export interface CheckboxProps extends Omit<CheckboxPrimitive.CheckboxProps, 'onChange'> {
  label?: string;
  indeterminate?: boolean;
  onChange?: (checked: boolean) => void;
}

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(function Checkbox(
  { label, indeterminate, checked, onChange, className, disabled, id, ...rest },
  ref,
) {
  const resolvedChecked = indeterminate ? 'indeterminate' : checked;

  return (
    // Com label, ancora na primeira linha (items-start + mt-0.5 centra o
    // quadrado de 16px na linha de 20px): quando o texto quebra — termo longo
    // em tela estreita — items-center jogaria o quadrado para o meio do
    // parágrafo. Sem label não há o que alinhar e o centro continua valendo.
    <div className={cn('flex gap-2', label ? 'items-start' : 'items-center')}>
      <CheckboxPrimitive.Root
        ref={ref}
        id={id}
        checked={resolvedChecked}
        onCheckedChange={(value) => {
          if (onChange) {
            onChange(value === true);
          }
        }}
        disabled={disabled}
        className={cn(
          'flex h-4 w-4 shrink-0 items-center justify-center rounded border border-slate-300 transition-colors',
          label && 'mt-0.5',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'data-[state=checked]:bg-primary-500 data-[state=checked]:border-primary-500 data-[state=checked]:text-white',
          'data-[state=indeterminate]:bg-primary-500 data-[state=indeterminate]:border-primary-500 data-[state=indeterminate]:text-white',
          className,
        )}
        {...rest}
      >
        <CheckboxPrimitive.Indicator className="flex items-center justify-center">
          {indeterminate ? <Minus className="h-3 w-3" /> : <Check className="h-3 w-3" />}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {label && (
        <label
          htmlFor={id}
          className={cn(
            'text-sm font-body text-slate-700 select-none',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          {label}
        </label>
      )}
    </div>
  );
});
